import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { useMemo } from 'react';

import { Bem } from '../../service/Bem/Bem';
import { ValidationErrorData } from '../../typings/common';
import { TagInputType, TargetingTagValue } from '../../typings/tagCloudTargeting';
import { Alert, AlertSize, AlertType } from '../Alert/Alert';
import { EditorControlsDecorator } from '../EditorControlsDecorator/EditorControlsDecorator';
import { ModalDecorator } from '../ModalDecorator/ModalDecorator';
import { TargetingTextEditor } from '../TargetingTextEditor/TargetingTextEditor';

import './PricePackagesClientsModal.css';
import * as keyset from './PricePackagesClientsModal.i18n';

const i18n = i18nFactory(keyset);

export interface PricePackagesClientsModalProps {
    isVisible: boolean;
    isProgress: boolean;
    title: string;
    errors?: ValidationErrorData[];
    tags: TargetingTagValue[];
    onClose: () => void;
    onDelete: (tag: TargetingTagValue) => void;
    onSave: () => void;
    onAdd: (tags: TargetingTagValue[]) => void;
}

const BLOCK = 'price-packages-clients-modal';

export const PricePackagesClientsModal: React.FC<PricePackagesClientsModalProps> = React.memo(props => {
    const { isVisible, onClose, tags, onAdd, onDelete, onSave, title, errors, isProgress } = props;
    if (!isVisible) {
        return null;
    }

    const getErrors = useMemo(() => {
        if (!(errors && errors.length)) {
            return null;
        }

        return errors.map((error, index) => {
            return (
                <Alert className={Bem.cls(BLOCK, 'alert')} type={AlertType.error} size={AlertSize.s} key={`${index}`}>
                    <div className={Bem.cls(BLOCK, 'alert-content')}>{error.text}</div>
                </Alert>
            );
        });
    }, [errors]);

    return (
        <ModalDecorator
            theme={'default'}
            header={i18n('title', { title: title })}
            hasClose={true}
            zIndexGroupLevel={1}
            autoclosable={true}
            scrollable={true}
            fixedWidth={650}
            visible={isVisible}
            onClose={onClose}
            progress={isProgress}
        >
            <TargetingTextEditor
                tags={tags}
                isEditable={true}
                onAdd={onAdd}
                onDelete={onDelete}
                type={TagInputType.INPUT}
            />
            {getErrors}
            <EditorControlsDecorator
                cls={Bem.cls(BLOCK, 'buttons')}
                submitButtonText={i18n('save')}
                cancelButtonText={i18n('cancel')}
                submitButtonTheme={'action'}
                onSubmitClick={onSave}
                onCancelClick={onClose}
            />
        </ModalDecorator>
    );
});
